import React, { useMemo, useState } from 'react';
import { Alert, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const allRoles = [
  { name: 'Admin', apiName: 'Administrator', description: 'Admin' },
  { name: 'Sales', apiName: 'Sales_Rep', description: 'Sales' },
];

const normalizeRoleName = (roleName = '') => {
  if (roleName === 'Administrator') return 'Admin';
  if (roleName === 'Sales_Rep') return 'Sales';
  return roleName;
};

const toApiRoleName = (roleName = '') => allRoles.find((role) => role.name === roleName)?.apiName || roleName;

const normalizeRoles = (value) => String(value || '')
  .split(',')
  .map((role) => normalizeRoleName(role.trim()))
  .filter(Boolean);

const normalizeMultiplier = (multiplier = {}) => ({
  ...multiplier,
  id: Number(getSalesId(multiplier) || 0),
  name: String(getSalesValue(multiplier, 'name', 'Name') || ''),
  displayName: String(getSalesValue(multiplier, 'displayName', 'DisplayName') || getSalesValue(multiplier, 'name', 'Name') || ''),
  roles: normalizeRoles(getSalesValue(multiplier, 'visibilityRoles', 'VisibilityRoles')).map((name) => ({
    name,
    description: allRoles.find((role) => role.name === name)?.description || name,
  })),
  value: getSalesValue(multiplier, 'value', 'Value') === null || getSalesValue(multiplier, 'value', 'Value') === undefined
    ? null
    : Number(getSalesValue(multiplier, 'value', 'Value')),
  description: String(getSalesValue(multiplier, 'description', 'Description', 'displayName', 'DisplayName', 'name', 'Name') || ''),
});

const normalizeMultipliers = (rows) => rows.map(normalizeMultiplier);

const MultiplierList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingValueId, setEditingValueId] = useState(null);
  const [editValueInput, setEditValueInput] = useState('');
  const [editingRolesId, setEditingRolesId] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [actionError, setActionError] = useState('');

  const {
    items: multipliers,
    setItems: setMultipliers,
    loading,
    error,
    request,
    endpoints,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.systemConfigurations.base, {
    normalize: normalizeMultipliers,
    errorMessage: 'Unable to load multipliers.',
  });

  const filteredMultipliers = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return multipliers;
    return multipliers.filter((multiplier) =>
      [multiplier.name, multiplier.displayName, multiplier.description]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [multipliers, searchTerm]);

  const saveMultiplier = async (multiplier, patch) => {
    const payload = {
      ...multiplier,
      ...patch,
      Id: multiplier.id,
      Name: patch.name ?? multiplier.name,
      DisplayName: patch.displayName ?? multiplier.displayName,
      Value: patch.value ?? multiplier.value,
      VisibilityRoles: patch.visibilityRoles ?? multiplier.roles.map((role) => toApiRoleName(role.name)).join(','),
    };

    await request({ method: 'PUT', url: endpoints.systemConfigurations.byId(multiplier.id), data: payload });
    setMultipliers((current) =>
      current.map((item) => (item.id === multiplier.id ? normalizeMultiplier(payload) : item))
    );
  };

  const handleStartEditValue = (multiplier) => {
    if (!canEditSales) return;
    setEditingValueId(multiplier.id);
    setEditValueInput(multiplier.value != null ? String(multiplier.value) : '');
  };

  const handleSaveValue = async (multiplier) => {
    const parsed = parseFloat(editValueInput);
    setActionError('');
    try {
      await saveMultiplier(multiplier, { value: Number.isNaN(parsed) ? null : parsed });
      setEditingValueId(null);
    } catch {
      setActionError('Unable to save multiplier value.');
    }
  };

  const handleStartEditRoles = (multiplier) => {
    if (!canEditSales) return;
    setEditingRolesId(multiplier.id);
    setEditRoles(multiplier.roles.map((role) => role.name));
  };

  const handleSaveRoles = async (multiplier) => {
    const nextRoles = allRoles.filter((role) => editRoles.includes(role.name));
    setActionError('');
    try {
      await saveMultiplier(multiplier, {
        visibilityRoles: nextRoles.map((role) => role.apiName).join(','),
      });
      setEditingRolesId(null);
    } catch {
      setActionError('Unable to save multiplier visibility.');
    }
  };

  const toggleRole = (roleName) => {
    setEditRoles((current) =>
      current.includes(roleName) ? current.filter((role) => role !== roleName) : [...current, roleName]
    );
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-multipliers-title">
      <Row className="mb-3">
        <Col><h4 id="sales-multipliers-title">Multiplier List</h4></Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control type="text" placeholder="Search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} style={{ paddingLeft: '40px' }} />
            </div>
          </Form.Group>
        </Col>
      </Row>

      {(error || actionError) && <Alert variant="danger">{error || actionError}</Alert>}

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr className="table-border">
                  <th style={{ width: '30%' }}>Name</th>
                  <th style={{ width: '27%' }}>Display Name</th>
                  <th>Visibility</th>
                  <th style={{ width: '9%' }}>Multiplier</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading multipliers...</div></td></tr>
                ) : filteredMultipliers.length === 0 ? (
                  <tr><td colSpan={4}><div className="sales-no-data">No Data Available</div></td></tr>
                ) : (
                  filteredMultipliers.map((multiplier) => (
                    <tr key={multiplier.id} className="table-border">
                      <td>{multiplier.name}</td>
                      <td>{multiplier.displayName}</td>
                      <td>
                        {editingRolesId === multiplier.id ? (
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {allRoles.map((role) => (
                              <Form.Check
                                key={role.name}
                                type="checkbox"
                                id={`role-${multiplier.id}-${role.name}`}
                                label={role.description}
                                checked={editRoles.includes(role.name)}
                                onChange={() => toggleRole(role.name)}
                                inline
                              />
                            ))}
                            <button type="button" className="edit-icon" title="Save" onClick={() => handleSaveRoles(multiplier)}>
                              <i className="bi bi-save"></i>
                            </button>
                          </div>
                        ) : (
                          <span>
                            {multiplier.roles.map((role) => <span key={role.name} className={`sales-user-badge status-${role.name}`}>{role.description}</span>)}
                            <button type="button" className="edit-icon" onClick={() => handleStartEditRoles(multiplier)} title="Edit" disabled={!canEditSales}>
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          </span>
                        )}
                      </td>
                      <td>
                        {editingValueId === multiplier.id ? (
                          <div className="d-flex align-items-center gap-2">
                            <Form.Control type="number" size="sm" value={editValueInput} onChange={(event) => setEditValueInput(event.target.value)} style={{ width: '80px' }} autoFocus />
                            <button type="button" className="edit-icon" title="Save" onClick={() => handleSaveValue(multiplier)}>
                              <i className="bi bi-save"></i>
                            </button>
                          </div>
                        ) : (
                          <span>
                            {multiplier.value != null ? multiplier.value : <em style={{ color: '#999' }}>Please enter Multiplier</em>}
                            <button type="button" className="edit-icon" onClick={() => handleStartEditValue(multiplier)} title="Edit" disabled={!canEditSales}>
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MultiplierList;
